/**
 * 学習計画APIサービス
 */

import api from './api'
import {
  LearningPlanResponse,
  LearningPlanListResponse,
  LearningPlanListFilter,
  CreateLearningPlanRequest,
  UpdateLearningPlanRequest,
  ScheduledTestResponse,
  LearningPlanProgressResponse,
  LearningPlanReportResponse,
} from '../types/learningPlan'

export class LearningPlanService {
  /**
   * 学習計画一覧を取得
   */
  static async getPlans(filter: LearningPlanListFilter = {}): Promise<LearningPlanListResponse> {
    const params = new URLSearchParams()
    if (filter.status) params.append('status', filter.status)
    if (filter.complianceRuleId) params.append('complianceRuleId', filter.complianceRuleId)
    if (filter.userGroupId) params.append('userGroupId', filter.userGroupId)
    if (filter.search) params.append('search', filter.search)
    if (filter.page) params.append('page', filter.page.toString())
    if (filter.limit) params.append('limit', filter.limit.toString())

    const queryString = params.toString()
    const url = queryString ? `/learning-plans?${queryString}` : '/learning-plans'
    return api.get<LearningPlanListResponse>(url)
  }

  /**
   * 学習計画詳細を取得
   */
  static async getPlan(id: string): Promise<LearningPlanResponse> {
    return api.get<LearningPlanResponse>(`/learning-plans/${id}`)
  }

  /**
   * 学習計画を作成
   */
  static async createPlan(data: CreateLearningPlanRequest): Promise<LearningPlanResponse> {
    return api.post<LearningPlanResponse>('/learning-plans', data)
  }

  /**
   * 学習計画を更新
   */
  static async updatePlan(
    id: string,
    data: UpdateLearningPlanRequest
  ): Promise<LearningPlanResponse> {
    return api.put<LearningPlanResponse>(`/learning-plans/${id}`, data)
  }

  /**
   * 学習計画を削除
   */
  static async deletePlan(id: string): Promise<void> {
    return api.delete(`/learning-plans/${id}`)
  }

  /**
   * 学習計画を有効化
   */
  static async activatePlan(id: string): Promise<LearningPlanResponse> {
    return api.post<LearningPlanResponse>(`/learning-plans/${id}/activate`)
  }

  /**
   * 学習計画を一時停止
   */
  static async pausePlan(id: string): Promise<LearningPlanResponse> {
    return api.post<LearningPlanResponse>(`/learning-plans/${id}/pause`)
  }

  /**
   * 学習計画をキャンセル
   */
  static async cancelPlan(id: string): Promise<LearningPlanResponse> {
    return api.post<LearningPlanResponse>(`/learning-plans/${id}/cancel`)
  }

  /**
   * スケジュール済みテスト一覧を取得
   */
  static async getScheduledTests(planId: string): Promise<ScheduledTestResponse[]> {
    return api.get<ScheduledTestResponse[]>(`/learning-plans/${planId}/scheduled-tests`)
  }

  /**
   * スケジュール済みテスト詳細を取得
   */
  static async getScheduledTest(id: string): Promise<ScheduledTestResponse> {
    return api.get<ScheduledTestResponse>(`/scheduled-tests/${id}`)
  }

  /**
   * スケジュール済みテストを承認
   */
  static async approveScheduledTest(
    id: string
  ): Promise<{ id: string; status: string; approvedBy: string; approvedAt: string }> {
    return api.post<{ id: string; status: string; approvedBy: string; approvedAt: string }>(
      `/scheduled-tests/${id}/approve`
    )
  }

  /**
   * スケジュール済みテストを却下
   */
  static async rejectScheduledTest(
    id: string,
    reason: string
  ): Promise<{ id: string; status: string; rejectedReason: string }> {
    return api.post<{ id: string; status: string; rejectedReason: string }>(
      `/scheduled-tests/${id}/reject`,
      { reason }
    )
  }

  /**
   * プラン進捗を取得
   */
  static async getPlanProgress(id: string): Promise<LearningPlanProgressResponse> {
    return api.get<LearningPlanProgressResponse>(`/learning-plans/${id}/progress`)
  }

  /**
   * レポート一覧を取得
   */
  static async getReports(planId: string): Promise<LearningPlanReportResponse[]> {
    return api.get<LearningPlanReportResponse[]>(`/learning-plans/${planId}/reports`)
  }

  /**
   * レポート詳細を取得
   */
  static async getReport(id: string): Promise<LearningPlanReportResponse> {
    return api.get<LearningPlanReportResponse>(`/learning-plan-reports/${id}`)
  }
}

export default LearningPlanService
